/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2018, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import { withRouter } from 'react-router-dom'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import qs from 'query-string'
import moment from 'moment'
import { isEqualWith } from 'lodash'

import {
  Spinner,
  Table,
  SpanWithData,
  PageNavigationButtons,
  RightArrow,
  Warning
} from '../'

import {
  PageNavigationControl,
  decodeBase64,
  saveCursor,
  getPageCursor
} from '../../utils'

class FileList extends React.PureComponent {
  static propTypes = {
    fileType: PropTypes.string,
    fileTypeName: PropTypes.string,
    processed: PropTypes.bool.isRequired,
    createdAtFrom: PropTypes.string,
    createdAtTo: PropTypes.string,
    orderBy: PropTypes.string,
    changeSort: PropTypes.func,
    onFileClick: PropTypes.func,
    withoutRequestRuns: PropTypes.bool,
    resultsPerPage: PropTypes.number,
    className: PropTypes.string
  }

  static defaultProps = {
    resultsPerPage: 15
  }

  constructor(props) {
    super(props)

    this.state = {
      pageNav: new PageNavigationControl(),
      filterChanged: false
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.hasFilterChanged(this.props, nextProps) && !sessionStorage.getItem('fileListPageState')) {
      let previousFilters = JSON.parse(sessionStorage.getItem('fileListFilters'))
      if (previousFilters && !this.hasFilterChanged(previousFilters, nextProps)) {
        sessionStorage.removeItem('fileListFilters')
      } else {
        this.setState({
          filterChanged: true
        }, () => {
          this.props.refetch()
          this.state.pageNav.reset()
        })
        sessionStorage.removeItem('fileListFilters')
      }
    } else {
      if (!nextProps.loading) {
        if (this.state.filterChanged) {
          this.setState({
            filterChanged: false
          })
          this.state.pageNav.setPageStates(null, nextProps.files)
        }
        else if (nextProps.files) {
          // NOTE: commented code below can be used again when we allow for prev/next file navigation directly from file/request processing modal
          // if (nextProps.location.search.length > 0) {
          //   let queryParams = qs.parse(nextProps.location.search)
          //   if (queryParams.processNext) {
          //     delete queryParams.processNext
          //     if (nextProps.files.edges[1]) {
          //       this.props.history.push({
          //         pathname: `/os/operations/general/files/${nextProps.files.edges[1].node.id}`,
          //         search: qs.stringify(Object.assign({}, queryParams))
          //       })
          //     } else {
          //       this.props.history.push('/os/operations/general/files')
          //     }
          //   }
          // }
          let fileListPageState = JSON.parse(sessionStorage.getItem('fileListPageState'))
          if (fileListPageState) {
            this.state.pageNav.setPageStates(null, null, fileListPageState)
            sessionStorage.removeItem('fileListPageState')
            this.props.refetch({ cursor: fileListPageState.cursor })
          } else {
            if (this.props.files && !isEqualWith(this.props.files.edges, nextProps.files.edges, this.ignoreS3ImageUrlAwsAuthParams)) {
              this.state.pageNav.setPageStates(this.props.files, nextProps.files)
            }
          }
        }
      }
    }
  }

  hasFilterChanged = (props, nextProps) => (
    props.fileType !== nextProps.fileType ||
    props.fileTypeShortCode !== nextProps.fileTypeShortCode ||
    props.fileTypeLanguage !== nextProps.fileTypeLanguage ||
    props.fileTypeAudience !== nextProps.fileTypeAudience ||
    props.processed !== nextProps.processed ||
    props.createdAtFrom !== nextProps.createdAtFrom ||
    props.createdAtTo !== nextProps.createdAtTo ||
    props.processed !== nextProps.processed ||
    props.showAllFiles !== nextProps.showAllFiles ||
    props.withoutRequestRuns !== nextProps.withoutRequestRuns ||
    props.orderBy !== nextProps.orderBy
  )

  ignoreS3ImageUrlAwsAuthParams = (value, otherValue) => {
    if (typeof value === 'string' && typeof otherValue === 'string' && value.includes('s3.amazonaws.com') && otherValue.includes('s3.amazonaws.com')) {
      let valueWithoutAWSAuthPart = value.substring(0, value.indexOf('X-Amz-Date='))
      let otherValueWithoutAWSAuthPart = otherValue.substring(0, otherValue.indexOf('X-Amz-Date='))
      if (valueWithoutAWSAuthPart === otherValueWithoutAWSAuthPart) {
        return true
      }
    }
  }

  getFiltersObjFromProps = () => (
    {
      fileType: this.props.fileType,
      fileTypeShortCode: this.props.fileTypeShortCode,
      fileTypeLanguage: this.props.fileTypeLanguage,
      fileTypeAudience: this.props.fileTypeAudience,
      processed: this.props.processed ? 'Yes' : 'No',
      createdAtFrom: this.props.createdAtFrom,
      createdAtTo: this.props.createdAtTo,
      showAllFiles: this.props.showAllFiles,
      withoutRequestRuns: this.props.withoutRequestRuns,
      orderBy: this.props.orderBy
    }
  )

  savePaginationAndFilterState = (startCursor, filters) => {
    sessionStorage.setItem('fileListPageState', JSON.stringify({
      firstPage: this.state.pageNav.firstPage,
      lastPage: this.state.pageNav.lastPage,
      currentPage: this.state.pageNav.currentPage,
      pages: this.state.pageNav.pages,
      cursor: saveCursor(startCursor)
    }))
    sessionStorage.setItem('fileListFilters', JSON.stringify(filters))
  }

  saveStateAndGoToDetailPage = (fileId, startCursor, onFileClick) => {
    let filters = this.getFiltersObjFromProps()
    this.savePaginationAndFilterState(startCursor, filters)
    if (onFileClick) {
      onFileClick(fileId, filters)
    } else {
      this.props.history.push({
        pathname: `/os/operations/general/files/${fileId}`,
        search: qs.stringify(filters)
      })
    }
  }

  render() {
    if (this.props.loading) {
      return <Spinner type='brand' />
    }

    if (this.props.error) {
      return (
        <div className={this.props.className ? this.props.className : ''}>
          <Table
            columns={['Type', 'Created', 'Source', 'File name', 'Processed', '']}
            rows={[
              <tr key='no_records'>
                <td colSpan={6}>
                  <Warning inline={true}>
                    Error loading file data.
                  </Warning>
                </td>
              </tr>
            ]}
            objectRows={true}
          />
        </div>
      )
    }

    if (this.props.files.edges.length === 0) {
      return (
        <div className={this.props.className ? this.props.className : ''}>
          <Table
            columns={['Type', 'Created', 'Source', 'File name', 'Processed', '']}
            rows={[
              <tr key='no_records'>
                <td colSpan={6}>
                  No records to display.
                </td>
              </tr>
            ]}
            objectRows={true}
          />
        </div>
      )
    } else {
      let pageNav = this.state.pageNav
      let files = this.props.files
      let startCursor = files.pageInfo.startCursor
      let totalCount = files.totalCount

      return (
        <div className={this.props.className ? this.props.className : ''}>
          <Table
            columns={['Type', 'Created', 'Source', 'File name', 'Processed', '']}
            rows={files.edges.map((file, index) => (
              <tr
                onClick={() => this.saveStateAndGoToDetailPage(file.node.id, startCursor, this.props.onFileClick)}
                key={`row_${file.node.id}`}>
                <td>
                  {file.node.filetype ? file.node.filetype.name : '-'}
                </td>
                <td>
                  {moment(file.node.createdAt).format('llll')}
                </td>
                <td>
                  {<SpanWithData forField='FileSource' searchKey={file.node.source} />}
                </td>
                <td>
                  {file.node.asset
                    .substr(file.node.asset.lastIndexOf('/') + 1)  // last part of file asset url (not really necessary if not other '__' in asset name)
                    .split('__').pop()  // get part after '__'
                  }
                </td>
                <td>
                  {file.node.processed ? 'Yes' : 'No'}
                </td>
                <td>
                  <a style={{ textDecoration: 'none' }}>
                    <RightArrow />
                  </a>
                </td>
              </tr>
            ))}
            objectRows={true}
            orderBy={this.props.orderBy}
            changeSort={this.props.changeSort}
            querySortableColumns={{
              'Created': {
                orderByName: 'created_at'
              }
            }}
          />
          <PageNavigationButtons
            onNextClick={() => pageNav.nextPage(this.props.loadNextFiles)}
            onPreviousClick={() => pageNav.previousPage(this.props.loadPreviousFiles)}
            previousDisabled={pageNav.firstPage}
            nextDisabled={pageNav.lastPage}
            startCursor={startCursor}
            totalCount={totalCount}
            resultsPerPage={this.props.resultsPerPage}
            resultsText='records'
            onPageNumberClick={pageNumber => this.props.refetch({ cursor: getPageCursor(pageNumber, this.props.resultsPerPage) })}
            className='slds-float_right slds-m-right_x-large slds-m-top_medium slds-m-bottom_large'
          />
        </div>
      )
    }
  }
}

const getFiles = gql`
  query($fileType: UUID, $fileTypeShortCode: String, $fileTypeLanguage: String, $fileTypeAudience: String, $processed: Boolean, $createdAtFrom: DateTime, $createdAtTo: DateTime, $cursor: String, $fileExtension: String!, $withoutRequestRuns: Boolean, $resultsPerPage: Int!, $orderBy: String) {
    files(filetype_Id: $fileType, filetype_Language: $fileTypeLanguage, filetype_Audience: $fileTypeAudience, filetype_ShortCode: $fileTypeShortCode, processed: $processed, createdAt_Gte: $createdAtFrom, createdAt_Lte: $createdAtTo, first: $resultsPerPage, after: $cursor, asset_Iendswith: $fileExtension, orderBy: $orderBy, trash: false, requestruns_Id_Isnull: $withoutRequestRuns) {
      totalCount
      edges {
        node {
          id
          createdAt
          source
          asset
          url
          processed
          filetype {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const getPreviousFiles = gql`
  query($fileType: UUID, $fileTypeShortCode: String, $fileTypeLanguage: String, $fileTypeAudience: String, $processed: Boolean, $createdAtFrom: DateTime, $createdAtTo: DateTime, $cursor: String, $fileExtension: String!, $withoutRequestRuns: Boolean, $resultsPerPage: Int!, $orderBy: String) {
    files(filetype_Id: $fileType, filetype_Language: $fileTypeLanguage, filetype_Audience: $fileTypeAudience, filetype_ShortCode: $fileTypeShortCode, processed: $processed, createdAt_Gte: $createdAtFrom, createdAt_Lte: $createdAtTo, last: $resultsPerPage, before: $cursor, asset_Iendswith: $fileExtension, orderBy: $orderBy, trash: false, requestruns_Id_Isnull: $withoutRequestRuns) {
      totalCount
      edges {
        node {
          id
          createdAt
          source
          asset
          url
          processed
          filetype {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        startCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

const withData = graphql(getFiles, {
  options: ({ fileType, fileTypeShortCode, fileTypeLanguage, fileTypeAudience, processed, createdAtFrom, createdAtTo, showAllFiles, withoutRequestRuns, resultsPerPage = 15, orderBy }) => ({
    variables: {
      fileType: fileType ? decodeBase64(fileType) : undefined,
      fileTypeShortCode,
      fileTypeLanguage,
      fileTypeAudience,
      processed,
      createdAtFrom,
      createdAtTo,
      fileExtension: showAllFiles ? '' : 'png',
      withoutRequestRuns,
      resultsPerPage,
      orderBy
    },
    fetchPolicy: 'network-only'
  }),
  props: ({ ownProps: { fileType, fileTypeShortCode, fileTypeLanguage, fileTypeAudience, processed, createdAtFrom, createdAtTo, showAllFiles, withoutRequestRuns, resultsPerPage = 15, orderBy }, data: { loading, error, files, fetchMore, refetch } }) => ({
    loading,
    error,
    files,
    refetch,
    loadNextFiles: () => {
      return fetchMore({
        query: getFiles,
        variables: {
          fileType: fileType ? decodeBase64(fileType) : undefined,
          fileTypeShortCode,
          fileTypeLanguage,
          fileTypeAudience,
          processed,
          createdAtFrom,
          createdAtTo,
          fileExtension: showAllFiles ? '' : 'png',
          withoutRequestRuns,
          resultsPerPage,
          orderBy,
          cursor: files.pageInfo.endCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.files.edges
          const pageInfo = fetchMoreResult.files.pageInfo

          return {
            files: {
              __typename: previousResult.files.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    },
    loadPreviousFiles: () => {
      return fetchMore({
        query: getPreviousFiles,
        variables: {
          fileType: fileType ? decodeBase64(fileType) : undefined,
          fileTypeShortCode,
          fileTypeLanguage,
          fileTypeAudience,
          processed,
          createdAtFrom,
          createdAtTo,
          fileExtension: showAllFiles ? '' : 'png',
          withoutRequestRuns,
          resultsPerPage,
          orderBy,
          cursor: files.pageInfo.startCursor
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult.files.edges
          const pageInfo = fetchMoreResult.files.pageInfo

          return {
            files: {
              __typename: previousResult.files.__typename,
              edges: [...newEdges],
              pageInfo
            }
          }
        }
      })
    }
  })
})

export default compose(
  withData,
  withRouter
)(FileList)



// WEBPACK FOOTER //
// ./src/components/file-list/index.js